import { useMutation, useQueryClient, useQuery } from 'react-query'
import { getApiErrorMessage, request } from 'Features/auth/axios-client'
import {
  DeleteOperationalPlanTypes,
  PlanDataTypes,
  UseCreateOperationalPlanTypes,
  PlanRowType,
  UpdateOperationalPlanTypes,
  UpdateOperationalPlanParamType,
  CloneOperationalPlanParamType,
  CloneOperationalPlanTypes,
  OperationalPlannerReturnTypes,
  UpdateOperationalPlanLockTypes,
  OperationalPlansAPIDataType,
  UpdatePlanAccessParams,
  DeleteOperationalPlanParamType,
} from './OperationalPlanner.types'
import {
  PLAN_ACCESS_TYPE,
  PLAN_STATUS_FETCH_TIMEOUT,
  defaultPlanAccessType,
} from 'Common/Constants/Constants'
import { WorkCentreServerData } from 'Features/WorkCentreSummary/WorkCentreSummary.types'
import { RunPlanVariables } from 'Features/EditSettings/EditSettings.types'
import { toast } from 'react-hot-toast'

const fetchWorkcentreStatus = async (planId: string) => {
  const { data } = await request({
    url: `/plan/${planId}/workcentre-run-status`,
    method: 'get',
  })
  return data?.data
}

export const useWorkcentreStatus = (planId: string) => {
  return useQuery(
    ['workcentre-plan', planId],
    () => fetchWorkcentreStatus(planId),
    {
      select: (data) => {
        if (data) {
          return data.map((workcentreRow: WorkCentreServerData) => {
            return {
              workCentre: workcentreRow.code,
              status: workcentreRow.status,
              sourceSite: workcentreRow.siteLabel
                ? workcentreRow.siteLabel
                : workcentreRow.siteKey,
              workCentreKey: workcentreRow.workCentreKey,
              planId: workcentreRow.PartitionKey,
              siteKey: workcentreRow.siteKey,
              planAccessType: workcentreRow.planAccessType
                ? workcentreRow.planAccessType
                : PLAN_ACCESS_TYPE.PUBLIC,
            }
          })
        }
        return []
      },
      cacheTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )
}
const createOperationalPlan = async (planData: PlanDataTypes) => {
  const { data } = await request({
    url: '/operational-plan',
    data: planData,
    method: 'post',
  })
  return data
}

export const useCreateOperationalPlan = ({
  onSuccessfulPlanCreation,
  onFailedPlanCreation,
}: UseCreateOperationalPlanTypes) => {
  const queryClient = useQueryClient()
  return useMutation(createOperationalPlan, {
    onSuccess: async (data) => {
      await queryClient.invalidateQueries(['operational-plans'])
      onSuccessfulPlanCreation(
        data?.data?.plan_id,
        data?.data?.unconstrainedPlan
      )
    },
    // The error goes to the page, which toasts the API's words (e.g. a
    // scenario plan refused where PLAN_CREATE_BACKEND=arm) (US150091).
    onError: async (error) => {
      await queryClient.invalidateQueries(['operational-plans'])
      onFailedPlanCreation(error)
    },
  })
}

const deleteOperationalPlan = async ({
  planId,
  planType,
}: DeleteOperationalPlanParamType) => {
  const { data } = await request({
    url: `/operational-plan/${planId}`,
    type: planType,
    method: 'delete',
  })
  return data
}

export const useDeleteOperationalPlan = ({
  onDeleteSuccess,
  onDeleteFailure,
}: DeleteOperationalPlanTypes) => {
  const queryClient = useQueryClient()
  return useMutation(deleteOperationalPlan, {
    onMutate: async ({ planId, planType }) => {
      // Need to check what happens to other requests when using this
      await queryClient.cancelQueries({
        queryKey: ['operational-plans', planType],
      })
      const { data: previousData } = queryClient.getQueryData([
        'operational-plans',
        planType,
      ])

      if (!previousData) {
        await queryClient.invalidateQueries(['operational-plans'])
      }

      return previousData.map((planRow: OperationalPlannerReturnTypes) => {
        if (planRow.RowKey === planId) {
          planRow.status = 'DELETING'
        }
        return planRow
      })

      // queryClient.setQueryData(['operational-plans'], newData)

      // return { previousData }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['operational-plans'])
      //onDeleteSuccess()
    },
    onError: (_onError, _previous, context) => {
      queryClient.setQueryData(['operational-plans'], context.previousData)
      queryClient.invalidateQueries(['operational-plans'])
      onDeleteFailure()
    },
  })
}

const fetchSourceSites = async () => {
  const { data } = await request({
    url: '/source-sites',
    method: 'get',
  })
  return data
}

export const useSourceSites = (tenant: string) => {
  return useQuery(['source-sites', tenant], () => fetchSourceSites(), {
    select: (data) => data?.data,
    cacheTime: 3600 * 48,
    refetchOnWindowFocus: false,
  })
}

export const fetchOperationalPlan = async (planType = 'operational-plans') => {
  const { data } = await request({
    url: `/operational-plan?planType=${planType}`,
    method: 'get',
  })
  return data
}

export const useOperationalPlan = (
  planType: 'operational-plans' | 'scenario-plans'
) => {
  return useQuery(
    ['operational-plans', planType],
    () => fetchOperationalPlan(planType),
    {
      select: (data) => {
        if (data?.data) {
          const operationalPlanData = data.data.map(
            (planRow: PlanRowType, index: number) => {
              const planData = JSON.parse(planRow?.planData ?? '{}')
              const siteData = JSON.parse(planRow?.workCentres ?? '{}')
              let parsedPlanAccess = {}
              try {
                parsedPlanAccess = JSON.parse(planRow.planAccessType as string)
              } catch (e) {
                parsedPlanAccess = {
                  ...defaultPlanAccessType,
                  planAccessType:
                    planRow.planAccessType ?? PLAN_ACCESS_TYPE.PUBLIC,
                }
              }

              return {
                id: index,
                planId: planRow.RowKey,
                ...planData,
                planName: planRow.planDescription,
                status: planRow.status,
                status_detail: planRow.status_detail,
                error: planRow.error,
                planGeneratedOn: planRow.created_on,
                planGeneratedBy: planRow.PartitionKey,
                siteData,
                runId: planRow.runId,
                planAccessType: parsedPlanAccess,
                planPublished: planRow.hasOwnProperty('planPublished')
                  ? planRow.planPublished
                  : false,
              }
            }
          )
          return {
            operationalPlanData,
            planningScenarioAccess: data?.planning_scenarios_access,
            reportsAccess: data?.reports_access,
            plannedProductionAccess: data?.planned_production_access,
          }
        }
      },
      onError: (err) => {
        console.log(err)
        toast.error('The request failed, system will retry in few moments.')
      },
      cacheTime: 0,
      refetchInterval: PLAN_STATUS_FETCH_TIMEOUT,
    }
  )
}

const updateOperationalPlan = async ({
  planId,
  planReName,
}: UpdateOperationalPlanParamType) => {
  const { data } = await request({
    url: `/operational-plan/${planId}`,
    method: 'put',
    data: {
      new_name: planReName,
    },
  })
  return data
}

export const useUpdateOperationalPlan = ({
  onUpdateSuccess,
  onUpdateFailure,
}: UpdateOperationalPlanTypes) => {
  const queryClient = useQueryClient()
  return useMutation(updateOperationalPlan, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['operational-plans'])
      onUpdateSuccess()
    },
    onError: async () => {
      await queryClient.invalidateQueries(['operational-plans'])
      onUpdateFailure()
    },
  })
}
/*****************Plan lock unlock */

const updatePlanSwitch = async ({
  planId,
  planAccessType,
  planType,
}: UpdatePlanAccessParams) => {
  const { data } = await request({
    url: `/operational-plan/${planId}/plan-access`,
    method: 'put',
    type: planType,
    data: { planAccessType },
  })
  return data
}

export const useUpdatePlanAccess = ({
  onSwicthLockSuccess,
  onSwitchLockFail,
}: UpdateOperationalPlanLockTypes) => {
  const queryClient = useQueryClient()
  return useMutation(updatePlanSwitch, {
    onMutate: async ({ planId, planType }) => {
      await queryClient.cancelQueries({
        queryKey: ['operational-plans', planType],
      })
      await queryClient.cancelQueries({
        queryKey: ['plan-details-user-access', planId],
      })
      const { data: previousData } = queryClient.getQueryData([
        'operational-plans',
        planType,
      ])

      return previousData.map((row: OperationalPlannerReturnTypes) => {
        if (row.RowKey === planId) {
          row.status = 'UPDATING PLAN ACCESS'
        }
        return row
      })
    },
    onSuccess: async ({ data }, variables) => {
      await queryClient.invalidateQueries([
        'operational-plans',
        variables.planType,
      ])
      onSwicthLockSuccess(`Plan access type chnaged to ${data.planAccessType}.`)
    },
    onError: async () => {
      await queryClient.invalidateQueries(['operational-plans'])
      onSwitchLockFail()
    },
  })
}

/******************** End plan lock */

/***********Clone */
const cloneOperationalPlan = async ({
  planId,
  planName,
  planType,
}: CloneOperationalPlanParamType) => {
  const { data } = await request({
    url: `/operational-plan/${planId}/clone`,
    method: 'post',
    type: planType,
    data: {
      plan_name: planName,
    },
  })
  return data
}

export const useCloneOperationalPlan = ({
  onCloneSuccess,
  onCloneFailure,
}: CloneOperationalPlanTypes) => {
  const queryClient = useQueryClient()
  return useMutation(cloneOperationalPlan, {
    onMutate: async ({ planId, planName, planType }) => {
      await queryClient.cancelQueries({
        queryKey: ['operational-plans', planType],
      })
      const { data: previousData } =
        queryClient.getQueryData<OperationalPlansAPIDataType>([
          'operational-plans',
          planType,
        ]) as OperationalPlansAPIDataType

      let newData = previousData.map(
        (planRow: OperationalPlannerReturnTypes) => {
          if (planRow.RowKey === planId) {
            planRow.status = 'BEING_CLONED'
          }
          return planRow
        }
      )

      const todayDate = new Date()
      const dd = String(todayDate.getDate()).padStart(2, '0')
      const mm = String(todayDate.getMonth() + 1).padStart(2, '0')
      const yyyy = todayDate.getFullYear()
      const today = dd + '/' + mm + '/' + yyyy

      let newRow = previousData.filter(
        (item: OperationalPlannerReturnTypes) => item.RowKey === planId
      )

      newRow = {
        ...newRow[0],
        status: 'CLONING',
        created_on: today,
        planDescription: planName,
        runId: Math.random() * 100000,
      }

      newData = [...newData, newRow]

      newData = newData.map((item: OperationalPlannerReturnTypes) => {
        const dateParts: string[] = item.created_on.split('/')

        return {
          ...item,
          DateStamp: new Date(
            +dateParts[2],
            parseInt(dateParts[1]) - 1,
            +dateParts[0]
          ),
        }
      })

      return newData.sort(
        (a: OperationalPlannerReturnTypes, b: OperationalPlannerReturnTypes) =>
          b.DateStamp - a.DateStamp || b.status.localeCompare(a.status)
      )
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries([
        'operational-plans',
        variables.planType,
      ])
      onCloneSuccess()
    },
    onError: async (_onError, previous, context) => {
      queryClient.setQueryData(
        ['operational-plans', previous.planType],
        context?.previousData
      )
      onCloneFailure()
    },
  })
}

export const initiateRunPlan = async ({
  planId,
  parameters,
}: RunPlanVariables) => {
  console.log('Initiate Run Plan')
  console.log(planId, parameters)

  const { data } = await request({
    url: `/plan/${planId}/run-plan`,
    data: parameters,
    method: 'post',
  })
  return data
}

export const useRunPlan = (
  onPlanRunInitiatedSucces: () => void,
  onPlanRunInitiatedFailed: (message: string) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(initiateRunPlan, {
    onMutate: async (planId, parameters) => {
      console.log('OnMutate')
      console.log(planId, parameters)

      console.log('Cancelling Queries')
      await queryClient.cancelQueries({
        queryKey: ['operational-plans', 'operational-plans'],
      })

      console.log('Getting Query Data')
      const { data: previousData } = queryClient.getQueryData([
        'operational-plans',
        'operational-plans',
      ])

      console.log('Previous Data')
      console.log(previousData)
      if (!previousData) {
        console.log('Invalidating operational-plans')
        await queryClient.invalidateQueries(['operational-plans'])
      }

      console.log('Updating new data for planId to Running')

      const newData = previousData.map(
        (planRow: OperationalPlannerReturnTypes) => {
          if (planRow.RowKey === planId) {
            planRow.status = 'RUNNING'
          }
          return planRow
        }
      )
      console.log('New Data')
      console.log(newData)

      console.log('Setting operational plans with updated Data')

      queryClient.setQueryData(['operational-plans'], newData)

      console.log('Finished Mutate Function')

      return { previousData }
    },

    // onSuccess, not onSettled: onSettled also runs on a failure, which
    // showed "Plan run initiated." beside the failure toast (US149969).
    onSuccess: async () => {
      onPlanRunInitiatedSucces()
    },
    onError: (error, _previous, context) => {
      // onMutate throws on an empty cache, and then there is no context:
      // reading context.previousData threw here, before the failure callback
      // (as F13 in EditSettings.data.ts).
      const previousData = (context as { previousData?: unknown } | undefined)
        ?.previousData
      if (previousData) {
        queryClient.setQueryData(['operational-plans'], previousData)
      }
      queryClient.invalidateQueries(['operational-plans'])
      onPlanRunInitiatedFailed(
        getApiErrorMessage(error, 'Plan run failed to initiate.')
      )
    },
  })
}
